import type { HTMLAttributes } from 'react';
import {
  faCalendarClock,
  faEyeSlash,
  faFile,
  faGrid2,
  faHouse,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Icon } from '@/components/ui/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PageType } from '@/types/page';

export interface PageIconProps {
  pageType?: PageType;
  isPublished: boolean;
  isScheduled?: boolean;
  isScheduleMissed?: boolean;
}

export function getIconForPageType(pageType?: PageType) {
  const resolvedPageType = pageType ?? PageType.BLANK;

  switch (resolvedPageType) {
    case PageType.HOME:
      return faHouse;
    case PageType.BLOG_INDEX:
    case PageType.CASE_RESULT_INDEX:
    case PageType.REVIEW_INDEX:
      return faGrid2;
    case PageType.CASE_RESULT:
    case PageType.BLANK:
    case PageType.BLOG:
      return faFile;
    default:
      return faFile;
  }
}

export function PageIcon({ pageType, isPublished, isScheduled, isScheduleMissed }: PageIconProps) {
  if (isScheduled || isScheduleMissed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex text-sidebar-foreground/70',
              isScheduleMissed && 'text-destructive',
            )}
          >
            <Icon icon={faCalendarClock} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{isScheduleMissed ? 'Schedule missed' : 'Scheduled'}</TooltipContent>
      </Tooltip>
    );
  }

  if (!isPublished) {
    return <Icon icon={faEyeSlash} className="text-sidebar-foreground/70" />;
  }
  const icon = getIconForPageType(pageType);
  return <Icon icon={icon} />;
}

export function UnpublishedChangesIndicator({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('ml-1 size-1.5 rounded-full bg-amber-500', className)} {...props} />
      </TooltipTrigger>
      <TooltipContent>Page has unpublished changes</TooltipContent>
    </Tooltip>
  );
}
